import { html } from '@acme/b2b/utils';
import { PaymentProcessError } from '@b2b/common/directives/payment/types';
import { ACMEErrorResponse, TODO } from '@b2b/types/core';
import angular, { IQService, IWindowService } from 'angular';
import { BehaviorSubject } from 'rxjs';
import invariant from 'tiny-invariant';
import { PaymentService, PaymentServiceModule } from '../payment/payment.service';
import { CheckoutData, SecureCheckoutProps } from './checkout.types';
import { OrderEditCoreData, OrderEditData, SecureOrderEditProps } from './edit-order.types';
import { OrgOrderCreateData, SecureOrgOrderProps } from './org-checkout.types';
import { OrderBookingCreateData, SecureOrderBookingCreateProps } from './rebook.types';
import { ACMECORSCheckoutApiError, PaymentData, SecureCheckoutConfig } from './types';

export class CheckoutProcessService {
  static $inject = ['$window', '$q', 'HelpersService', 'ThemesHelperService', 'PaymentService'];

  /** Store */
  private acmePaymentProcessor: TODO | null = null;
  /** instance */
  private acmePaymentInstance: TODO | null = null;
  private paymentDataSubject = new BehaviorSubject<PaymentData | null>(null);

  constructor(
    private $window: IWindowService,
    private $q: IQService,
    private helpersService: TODO,
    private themesHelperService: TODO,
    private paymentService: PaymentService,
  ) {}

  isEnabledCORSCheckout(): boolean {
    return this.themesHelperService.getFeatureFlag('enableCORSCheckout');
  }

  initACMECheckout(): void {
    this.acmePaymentProcessor = this.helpersService.path(this.$window, ['ACMECheckout', 'ACMECheckout'], null);

    if (this.acmePaymentProcessor) {
      this.acmePaymentInstance = this.acmePaymentProcessor.create(this.getAcmePaymentConfig());
    }
  }

  getCheckoutLoadingHtml() {
    const loadingGifAsDataUri = `data:image/gif;base64,R0lGODlhEgAPAPIAAABf6v///////6LE9kqN7wAAAAAAAAAAACH/C05FVFNDQVBFMi4wAwEAAAAh/hpDcmVhdGVkIHdpdGggYWpheGxvYWQuaW5mbwAh+QQJBQAAACwAAAAAEgAPAAADHAi63P4wykmrvThXIS7n1tcBAwWSQwkQBKVqTgIAIfkECQUAAAAsAAAAABIADwAAAx4Iutz+MMpJq23iAsF11sowXKJolSNAUKZKrBcMPgkAIfkECQUAAAAsAAAAABIADwAAAxwIutz+MEogxLw4q6HB+B3XKQShlWWGmio7vlMCACH5BAkFAAAALAAAAAASAA8AAAMXCLrcvuLJ+cagOGtHtiKgB3RiaZ5oiiUAIfkECQUAAAAsAAAAABIADwAAAxQIuty+48knJCEz6827/2AojiSYAAAh+QQJBQAAACwAAAAAEgAPAAADFAi63L7kyTemvTgvobv/YCiOJJAAACH5BAkFAAAALAAAAAASAA8AAAMTCLrc/jAqIqu9duDNu4/CJ45XAgAh+QQJBQAAACwAAAAAEgAPAAADFAi63P4wykmrBeTqzTsbHiUIIZcAACH5BAkFAAAALAAAAAASAA8AAAMXCLrc/jDKSau9OOvtiBSYICrDQIFckwAAOwAAAAAAAAAAAA==`;
    return html`
      <p><img src="${loadingGifAsDataUri}" role="presentation" /> Please wait while the order is being processed.</p>
    `;
  }

  private getAcmePaymentConfig(): SecureCheckoutConfig {
    return {
      mid: this.themesHelperService.getCompany('mid'),
      urls: {
        b2bApi: {
          value: this.$window.CONFIG.B2B_API_BASE_URL,
          publishableKey: this.themesHelperService.getCompany('b2bPublishableKey'),
        },
        paymentsApi: {
          value: this.$window.CONFIG.PAYMENTS_API_BASE_URL,
          publishableKey: this.themesHelperService.getCompany('paymentsPublishableKey'),
        },
        checkout: {
          value: this.$window.CONFIG.CHECKOUT_BASE_URL,
        },
      },
      environment: this.$window.CONFIG.environment || 'production',
    };
  }

  handleACMEPaymentSession(data: SecureCheckoutProps) {
    const q = this.$q.defer<TODO>();

    if (!this.acmePaymentProcessor) {
      q.reject({
        status: 0,
        data: {
          code: 0,
          errorCode: 0,
          error: '',
        },
      });

      return q.promise;
    }

    this.acmePaymentInstance.b2b
      .checkout(data)
      .then((response) => {
        q.resolve(response);
      })
      .catch((response) => {
        q.reject(response);
      });

    return q.promise;
  }

  handleACMEPaymentSessionPayOrder(data: TODO) {
    const q = this.$q.defer<TODO>();

    if (!this.acmePaymentProcessor) {
      q.reject({
        status: 0,
        data: {
          code: 0,
          errorCode: 0,
          error: '',
        },
      });

      return q.promise;
    }

    this.acmePaymentInstance.b2b
      .editOrder(data)
      .then((response) => {
        q.resolve(response);
      })
      .catch((response) => {
        q.reject(response);
      });

    return q.promise;
  }

  handleACMEPaymentSessionEditOrder(data: SecureOrderEditProps) {
    const q = this.$q.defer<TODO>();

    if (!this.acmePaymentProcessor) {
      q.reject({
        status: 0,
        data: {
          code: 0,
          errorCode: 0,
          error: '',
        },
      });

      return q.promise;
    }

    this.acmePaymentInstance.b2b
      .editOrder(data)
      .then((response) => {
        q.resolve(response);
      })
      .catch((response) => {
        q.reject(response);
      });

    return q.promise;
  }

  handleACMEPaymentSessionRebookOrder(data: SecureOrderBookingCreateProps) {
    const q = this.$q.defer<TODO>();

    if (!this.acmePaymentProcessor) {
      q.reject({
        status: 0,
        data: {
          code: 0,
          errorCode: 0,
          error: '',
        },
      });

      return q.promise;
    }

    this.acmePaymentInstance.b2b
      .rebook(data)
      .then((response) => {
        q.resolve(response);
      })
      .catch((response) => {
        q.reject(response);
      });

    return q.promise;
  }

  handleACMEPaymentSessionOrgOrder(data: SecureOrgOrderProps) {
    const q = this.$q.defer<TODO>();

    if (!this.acmePaymentProcessor) {
      q.reject({
        status: 0,
        data: {
          code: 0,
          errorCode: 0,
          error: '',
        },
      });

      return q.promise;
    }

    this.acmePaymentInstance.b2b
      .orgCheckout(data)
      .then((response) => {
        q.resolve(response);
      })
      .catch((response) => {
        q.reject(response);
      });

    return q.promise;
  }

  getCORSCheckoutCSSVariables() {
    return {
      '--acme-font-family': `'Open Sans', sans-serif`,
      '--acme-primary-color': '#2291ff',
      '--acme-error-color': '#f00',
      '--acme-background-color': '#ffffff',
      '--acme-color': '#333333',
      '--acme-link-color': '#307bb8',
      '--acme-link-hover-color': '#222222',
      '--acme-dialog-header-background-color': '#0677ff',
      '--acme-dialog-header-color': '#ffffff',
      '--acme-dialog-header-font-size': '128.6%',
      '--acme-dialog-button-primary-background-color': '#005fea',
      '--acme-dialog-button-primary-border-color': '#0055d1',
      '--acme-dialog-button-primary-color': '#ffffff',
      '--acme-dialog-button-primary-font-size': '14px',
      '--acme-dialog-button-primary-text-transform': 'none',
      '--acme-dialog-button-text-font-size': '14px',
      '--acme-dialog-button-text-text-transform': 'none',
      '--acme-input-background-color': '#ffffff',
      '--acme-input-border-radius': '0',
      '--acme-input-border': '1px solid #cccccc',
      '--acme-input-box-shadow': 'inset 0 1px 1px rgba(0, 0, 0, 0.075)',
      '--acme-input-color': '#686868',
      '--acme-input-focus-border': '1px solid #0066ff',
      '--acme-input-focus-box-shadow': '0 0 10px #0066ff',
      '--acme-input-font-size': '14px',
      '--acme-input-font-weight': '400',
      '--acme-input-height': '44px',
      '--acme-input-label-color': '#666',
      '--acme-input-focus-label-color': '#767676',
      '--acme-input-label-font-weight': '400',
      '--acme-input-padding': '15px 10px',
    };
  }

  processCheckoutData(props: TODO, options: { withCreditCardInfo: boolean }) {
    const data: CheckoutData = {
      shoppingCartId: props.shoppingCartId,
      notes: props.notes,
      customerId: props.customerId,
      // accountId: props.accountId,
      membershipId: props.membershipId || null,
      // ticketDeliveryEmail: props.ticketDeliveryEmail,
      ticketDelivery: props.ticketDelivery,
      invoice: props.invoiceAfterEvent,
      emailLanguage: props.emailLanguage,
      paymentId: props.paymentId,
      paymentMethod: props.paymentMethod,
      /**
       * There is an issue when trying to transact as a member and selecting events with coupon discounts.
       * The cart is interpreting the charge amount as a partial payment.
       * @link https://acmeticketing.atlassian.net/browse/ENG-13639
       */
      // chargeAmount: props.chargeAmount,
      noEmail: props.noEmail,
      sendEmailToAll: props.sendEmailToAll,
      orderRequestId: props.orderRequestId,
      reservationId: props.reservationId || null,
      // giftCardNumber: props.giftCardNumber,
      membershipInfo: props.membershipInfo,
      checkoutForms: props.checkoutForms,
      emailAnalytics: props.emailAnalytics,
    };

    if (props.billingContact) {
      data.billingFirstName = props.billingContact.firstName;
      data.billingLastName = props.billingContact.lastName;
      data.billingEmail = props.billingContact.email;
      data.billingPhoneNumber = props.billingContact.phoneNumber;

      if (props.billingContact.address) {
        data.billingAddress1 = props.billingContact.address.streetAddress1;
        data.billingAddress2 = props.billingContact.address.streetAddress2;
        data.billingCity = props.billingContact.address.city;
        data.billingState = props.billingContact.address.state;
        data.billingCountry = props.billingContact.address.country;
        data.billingZipCode = props.billingContact.address.zipCode;
      }
    }

    if (props.organizationContact) {
      data.accountContactId = props.organizationContact.contactId;
      data.contactFirstName = props.organizationContact.firstName;
      data.contactLastName = props.organizationContact.lastName;
      data.email = props.organizationContact.email;
      data.phoneNumber = props.organizationContact.phoneNumber;

      if (props.organizationContact.address) {
        data.address1 = props.organizationContact.address.streetAddress1;
        data.address2 = props.organizationContact.address.streetAddress2;
        data.city = props.organizationContact.address.city;
        data.state = props.organizationContact.address.state;
        data.country = props.organizationContact.address.country;
        data.zipCode = props.organizationContact.address.zipCode;
      }
    }

    if (props.requestUUID) {
      data.requestUUID = props.requestUUID;
    }

    if (options.withCreditCardInfo) {
      data.acmeToken = props.acmeToken;
      data.manualEntryCardNumber = props.manualEntryCardNumber;
      data.expDate = props.expDate;
      data.ccLastFourDigits = props.ccLastFourDigits;
      data.cvc = props.cvc;
    }

    return data;
  }

  processOrderUpdateData(props: TODO, options: { withCreditCardInfo: boolean }) {
    const data: OrderEditData = {
      orderId: props.orderId,
      customerId: props.customerId,
      accountId: props.accountId,
      accountName: props.accountName,
      accountContactId: props.accountContactId,
      notes: props.notes,
      orderItems: props.orderItems,
      noEmail: props.noEmail,
      invoice: props.invoiceAfterEvent,
      sendEmailToAll: props.sendEmailToAll,
      payment: props.payment,
      email: props.email,
      contactFirstName: props.contactFirstName,
      contactLastName: props.contactLastName,
      zipCode: props.zipCode,
      phoneNumber: props.phoneNumber,
      billingEmail: props.billingEmail,
      billingFirstName: props.billingFirstName,
      billingLastName: props.billingLastName,
      billingPhoneNumber: props.billingPhoneNumber,
    };

    if (props.address) {
      data.billingAddress1 = props.address.streetAddress1;
      data.billingAddress2 = props.address.streetAddress2;
      data.billingCity = props.address.city;
      data.billingState = props.address.state;
      data.billingCountry = props.address.country;
      data.billingZipCode = props.address.zipCode;
    }

    if (props.requestUUID) {
      data.requestUUID = props.requestUUID;
    }

    if (options.withCreditCardInfo === false && data.payment) {
      delete data.payment.acmeToken;
      delete data.payment.ccLastFourDigits;
      delete data.payment.creditCardBrand;
      delete data.payment.cvc;
      delete data.payment.expDate;
      delete data.payment.manualEntryCardNumber;
    }

    if (props.forms && props.forms.length) {
      data.forms = props.forms;
    }

    if (props.giftCardAuthorizationIds && props.giftCardAuthorizationIds.length) {
      data.giftCardAuthorizationIds = props.giftCardAuthorizationIds;
    }

    return data;
  }

  processOrderUpdateCoreData(props: TODO) {
    const data: OrderEditCoreData = {
      orderId: props.orderId,
      customerId: props.customerId,
      accountId: props.accountId,
      accountName: props.accountName,
      accountContactId: props.accountContactId,
      notes: props.notes,
      orderItems: props.orderItems,
      noEmail: props.noEmail,
      invoice: props.invoiceAfterEvent,
      sendEmailToAll: props.sendEmailToAll,
      email: props.email,
      contactFirstName: props.contactFirstName,
      contactLastName: props.contactLastName,
      zipCode: props.zipCode,
      phoneNumber: props.phoneNumber,
      billingEmail: props.billingEmail,
      billingFirstName: props.billingFirstName,
      billingLastName: props.billingLastName,
      billingPhoneNumber: props.billingPhoneNumber,
    };

    if (props.address) {
      data.billingAddress1 = props.address.streetAddress1;
      data.billingAddress2 = props.address.streetAddress2;
      data.billingCity = props.address.city;
      data.billingState = props.address.state;
      data.billingCountry = props.address.country;
      data.billingZipCode = props.address.zipCode;
    }

    if (props.requestUUID) {
      data.requestUUID = props.requestUUID;
    }

    const processData = this.paymentService.getProcessData();

    /**
     * APP-3212: if no payment due, dont send cc info
     */
    if (processData.chargeAmount !== 0) {
      data.payment = props.payment;
    }

    if (props.forms && props.forms.length) {
      data.forms = props.forms;
    }

    if (props.giftCardAuthorizationIds && props.giftCardAuthorizationIds.length) {
      data.giftCardAuthorizationIds = props.giftCardAuthorizationIds;
    }

    return data;
  }

  processOrderBookingCreateData(props: TODO, options: { withCreditCardInfo: boolean }): OrderBookingCreateData {
    const data: OrderBookingCreateData = {
      email: props.email,
      orderId: props.orderId,
      customerId: props.customerId,
      notes: props.notes,
      rebookItems: props.rebookItems,
      incidentReasonCode: props.incidentReasonCode,
      eventRebookFeeCounts: props.eventRebookFeeCounts,
      noEmail: props.noEmail,
      invoice: props.invoiceAfterEvent,
      sendEmailToAll: props.sendEmailToAll,
      payment: props.payment,
    };

    const processData = this.paymentService.getProcessData();

    if (props.address) {
      data.billingAddress1 = props.address.streetAddress1;
      data.billingAddress2 = props.address.streetAddress2;
      data.billingCity = props.address.city;
      data.billingState = props.address.state;
      data.billingCountry = props.address.country;
      data.billingZipCode = props.address.zipCode;
    }

    if (options.withCreditCardInfo === false) {
      delete data.payment.acmeToken;
      delete data.payment.ccLastFourDigits;
      delete data.payment.creditCardBrand;
      delete data.payment.cvc;
      delete data.payment.expDate;
      delete data.payment.manualEntryCardNumber;
    }

    if (processData.paymentType === 'manual-zero') {
      data.payment.type = 'Cash';
    }

    return data;
  }

  processOrdersPartnersCreateData(props: TODO, options: { withCreditCardInfo: boolean }): OrgOrderCreateData {
    const data: OrgOrderCreateData = {
      accountId: props.accountId,
      notes: props.notes,
      events: props.events,
      customerId: props.customerId,
      paymentId: props.paymentId,
      ticketDelivery: props.ticketDelivery,
      invoice: props.invoiceAfterEvent,
      saleChannel: props.saleChannel,
      accountContactId: props.accountContactId,
      contactFirstName: props.accountContactFirstName,
      contactLastName: props.accountContactLastName,
      contactEmail: props.accountContactEmail,
      phoneNumber: props.accountContactPhoneNumber,
      chargeAmount: props.chargeAmount,
      noEmail: props.noEmail,
      sendEmailToAll: props.sendEmailToAll,
      orderRequestId: props.orderRequestId,
      reservationId: props.reservationId || null,
    };

    if (props.requestUUID) {
      data.requestUUID = props.requestUUID;
    }

    if (props.giftCardAuthorizationIds && props.giftCardAuthorizationIds.length) {
      data.giftCardAuthorizationIds = props.giftCardAuthorizationIds;
    }

    const processData = this.paymentService.getProcessData();

    data.billingPhoneNumber = props.phoneNumber;
    data.billingEmail = props.contactEmail;
    data.billingLastName = props.contactLastName;
    data.billingFirstName = props.contactFirstName;

    if (props.address) {
      data.billingAddress1 = props.address.streetAddress1;
      data.billingAddress2 = props.address.streetAddress2;
      data.billingCity = props.address.city;
      data.billingState = props.address.state;
      data.billingCountry = props.address.country;
      data.billingZipCode = props.address.zipCode;
    }

    /**
     * APP-3212: if no payment due, dont send cc info and billing info
     */
    if (processData.chargeAmount !== 0) {
      data.paymentMethod = props.paymentMethod;

      if (options.withCreditCardInfo) {
        data.acmeToken = props.acmeToken;
        data.ccLastFourDigits = props.ccLastFourDigits;
        data.creditCardBrand = props.creditCardBrand;
        data.cvc = props.cvc;
        data.expDate = props.expDate;
        data.manualEntryCardNumber = props.manualEntryCardNumber;
      }
    }

    return data;
  }

  setPaymentData(data: TODO): void {
    this.paymentDataSubject.next(data);
  }

  getPaymentData() {
    return this.paymentDataSubject.value;
  }

  shouldUseCORSCheckout(options: { requiresCardData: boolean }): boolean {
    return this.isEnabledCORSCheckout() && this.paymentService.isCreditCard && options.requiresCardData;
  }

  mapToPaymentProcessError(sdkApiError: ACMECORSCheckoutApiError): { status: number; data: PaymentProcessError } {
    invariant(sdkApiError.type === 'api.error');

    switch (sdkApiError.params.type) {
      case 'NETWORK_ERROR':
      case 'TIMEOUT_ERROR':
      case 'TOO_MANY_REQUESTS':
      case 'UNKNOWN_ERROR': {
        return {
          status: 0,
          data: {
            code: 0,
            errorCode: 0,
            error: sdkApiError.params.message,
            errors: undefined,
          },
        };
      }

      default: {
        const params = sdkApiError.params as ACMEErrorResponse;

        return {
          status: 0,
          data: {
            code: params.errorCode || 0,
            errorCode: params.errorCode || 0,
            error: params.message || '',
            errors: params.errors,
          },
        };
      }
    }
  }
}

export const CheckoutProcessServiceModule = angular
  .module('acme.common.service.checkoutProcess', [
    'acme.common.service.themesHelper',
    'acme.common.service.helpers',
    PaymentServiceModule,
  ])
  .service('CheckoutProcessService', CheckoutProcessService).name;
